import type { FAQItem } from './types';

export const featuresFAQs: FAQItem[] = [
  {
    id: 'web-app-only',
    question: 'Is there a native mobile app?',
    answer: `This is a web application. No native mobile apps are required or planned. A full installable, offline-capable Progressive Web App experience (home screen install, offline caching, push notifications) is a design goal, not built yet; today the platform runs in any modern mobile browser.`,
    category: 'features',
    featured: true,
  },
  {
    id: 'work-orders-overview',
    question: 'How does the work order system function?',
    answer: `The work order system is designed to streamline maintenance and service requests:

• **Resident Submission:** Take photos, describe issues, set priority levels
• **Management Triage:** Review, assign vendors, track progress
• **Vendor Portal:** Accept jobs, update status, upload completion photos
• **Automated Workflows:** Status updates, deadline tracking, resident notifications
• **Quality Control:** Resident approval process and feedback system

This design creates accountability and transparency for all parties involved. It is on the platform roadmap; work orders do not yet persist in the live product.`,
    category: 'features',
  },
  {
    id: 'communication-hub',
    question: 'How does the community communication system work?',
    answer: `The platform is designed to provide multiple communication channels:

• **Board Announcements:** Official notices sent to all residents
• **Event Notifications:** RSVP tracking, reminders, and updates
• **Emergency Alerts:** Urgent notices for time-sensitive matters
• **Discussion Forums:** Community topics with moderated discussions
• **Direct Messaging:** Secure resident-to-board communication
• **Maintenance Updates:** Work order status notifications

Logged, searchable communication history is a design goal. Push notifications and persisted messaging are not yet built; this describes the intended experience, not what is live today.`,
    category: 'features',
  },
  {
    id: 'mobile-app-features',
    question: 'What features are available on mobile devices?',
    answer: `The platform is mobile-responsive today, no app download required. On the roadmap, mobile use is intended to include:

• **Quick Actions:** Submit work orders, book facilities, pay assessments from any browser
• **Photo Integration:** Snap and upload photos directly for requests (camera access via browser)
• **Install to Home Screen:** Add a platform icon to the phone home screen for an app-like experience (no App Store/Google Play required)

**Not yet built:** push notifications, offline mode, and mobile digital-wallet payments. These require a Progressive Web App service worker and a payment processor integration, neither of which exists in the platform today.

**Web App Benefits (already true):**
• Universal access on iPhone, Android, tablets, laptops, any modern browser
• No app download or App Store approval delays
• Instant updates without waiting for app store review
• Lower costs (no Apple Developer or Google Play fees)`,
    category: 'features',
  },
  {
    id: 'financial-management',
    question: 'What financial management features are included?',
    answer: `The platform is designed to provide a comprehensive set of financial tools for HOA/community management. None of the following is live in the product today; a payment processor, bank data integration, and persistent financial records are still to be built.

**Collections & Receivables (planned):**
• Automated collections for assessments, fines, and fees
• Accounts receivable tracking with aging reports
• Automated payment reminders and late notices
• Payment plan management for residents

**Payment Collection (planned):**
• Monthly assessments and special assessments
• Violation fines and penalties (HOA-controlled rates)
• New resident setup/transfer fees
• Architectural review application fees
• Facility booking fees and security deposits
• Late payment penalties
• Any other fees defined in your bylaws

**Accounts Payable (planned):**
• Vendor invoice management and approval workflows
• Payment scheduling
• Vendor payment history tracking

**Payment Processing (planned):**
• ACH and credit/debit card support through a third-party payment processor (not yet integrated)
• Configurable convenience fee pass-through
• Refund/deposit return processing

**Bank Integrations (planned, Gold Package):**
• Connect bank accounts for balance visibility
• Transaction feeds from connected accounts
• Bank reconciliation support

**Financial Reporting (planned):**
• Ledger and account balances
• Transaction history and audit trails
• Budget tracking and variance reporting
• Receivables and payables aging reports

**Role-Specific Financial Dashboards (planned):**
• Board member dashboard with financial oversight
• Treasurer dashboard with detailed financial controls
• Resident dashboard showing account balance and payment history
• Vendor dashboard for invoicing and payment tracking

**Important:** You would control what fees to charge and collection policies; we provide the tools, you set the rules based on your community's bylaws. Ask us for current build status before relying on any specific financial feature for launch planning.`,
    category: 'features',
  },
  {
    id: 'role-dashboards',
    question: 'What dashboards are available for different user roles?',
    answer: `The platform is designed to provide customized dashboards tailored to each user role. The dashboards below describe the design intent; the underlying data (financial records, work orders, bookings, marketplace activity) does not yet persist, so live figures are not available today.

**Board Member Dashboard (planned):**
• Governance overview (pending votes, proposals, compliance status)
• Financial summary (receivables, payables, treasury balance)
• Recent activity feed across all platform areas
• Quick access to meeting management and voting

**Treasurer/Financial Dashboard (planned):**
• Bank account balances (pending a bank data integration)
• Accounts receivable aging and collections status
• Accounts payable and upcoming payments
• Budget vs. actual spending analysis

**Resident Dashboard (planned):**
• Personal account balance and payment history
• Upcoming assessments and due dates
• Active work orders and service requests
• Facility bookings and reservations
• Community announcements and events
• Voting on active proposals

**Vendor Dashboard (planned):**
• Active work orders assigned to vendor
• Invoice submission and payment tracking
• Service history and performance ratings
• Communication with property management

**Property Manager Dashboard (planned):**
• Overview of all work orders and maintenance requests
• Vendor performance and coordination
• Resident communication center
• Facility booking calendar
• Document management

Dashboards are intended to be mobile-optimized. Real-time updates depend on the persistence and integrations described above, which are not yet built.`,
    category: 'features',
  },
];
